import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { inr } from "../../lib/format";
import { Plus, Trash, Coins, Receipt, Wrench, Bank } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * CashflowLedger — per-property add/list/delete cashflow rows + side-by-side
 * Simple vs True XIRR display. Used inside expanded Property cards.
 *
 * Props:
 *   propertyId : str
 *   propertyName: str
 */
const TYPES = [
  { key: "rental_income",     label: "Rental income",      sign: "+", icon: Coins },
  { key: "other_income",      label: "Other income",       sign: "+", icon: Coins },
  { key: "renovation_expense", label: "Renovation",        sign: "−", icon: Wrench },
  { key: "loan_interest_paid", label: "Loan interest",     sign: "−", icon: Bank },
  { key: "property_tax",      label: "Property tax",       sign: "−", icon: Receipt },
  { key: "other_expense",     label: "Other expense",      sign: "−", icon: Receipt },
];
const INCOME_KEYS = new Set(["rental_income", "other_income"]);

export default function CashflowLedger({ propertyId, propertyName }) {
  const [items, setItems] = useState([]);
  const [xirr, setXirr] = useState(null);
  const [type, setType] = useState("rental_income");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: list }, { data: x }] = await Promise.all([
        api.get(`/networth/properties/${propertyId}/cashflows`),
        api.get(`/networth/properties/${propertyId}/xirr`),
      ]);
      setItems(list.items || []);
      setXirr(x);
    } finally { setLoaded(true); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!amount || !date) {
      toast.error("Amount and date are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/networth/properties/${propertyId}/cashflows`, {
        cashflow_type: type,
        amount: Number(amount),
        cashflow_date: date,
        notes: notes.trim(),
      });
      setAmount(""); setNotes("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add cashflow");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm("Delete this cashflow entry?")) return;
    try {
      await api.delete(`/networth/properties/${propertyId}/cashflows/${id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  if (!loaded) return <div className="text-xs text-muted-foreground">Loading cashflows…</div>;

  // Compute net
  const net = items.reduce((acc, r) => {
    const a = Number(r.amount) || 0;
    return acc + (INCOME_KEYS.has(r.cashflow_type) ? Math.abs(a) : -Math.abs(a));
  }, 0);

  return (
    <div className="space-y-4 mt-3" data-testid={`cashflow-ledger-${propertyId}`}>
      {/* XIRR side-by-side */}
      {xirr && (
        <div className="grid grid-cols-2 gap-3">
          <div className="border hairline p-3">
            <div className="eyebrow text-[10px]">Simple XIRR</div>
            <div className="font-serif text-xl mt-1" data-testid="cashflow-simple-xirr">
              {xirr.simple_xirr_pct}%
              <span className="text-xs text-muted-foreground"> p.a.</span>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              Purchase price → current value only
            </div>
          </div>
          <div className="border hairline p-3 bg-[hsl(var(--primary))]/5">
            <div className="eyebrow text-[10px]">True XIRR</div>
            <div className="font-serif text-xl mt-1 text-[hsl(var(--primary))]"
              data-testid="cashflow-true-xirr">
              {xirr.true_xirr_pct}%
              <span className="text-xs"> p.a.</span>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              Includes {xirr.n_cashflows} cashflow{xirr.n_cashflows === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      )}

      {xirr && xirr.n_cashflows === 0 && (
        <div className="text-xs text-muted-foreground italic" data-testid="cashflow-empty-note">
          Add cashflows below (rent, interest paid, property tax, renovation cost) to see a
          more accurate True XIRR.
        </div>
      )}

      {/* Ledger rows */}
      {items.length > 0 && (
        <div className="space-y-1" data-testid="cashflow-rows">
          {items.map((r) => {
            const isIncome = INCOME_KEYS.has(r.cashflow_type);
            return (
              <div key={r.id}
                className={`flex items-center gap-2 px-2 py-1.5 text-xs border hairline ${
                  isIncome ? "bg-green-500/5" : "bg-red-500/5"}`}>
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                  isIncome ? "bg-green-500/15 text-green-700" : "bg-red-500/15 text-red-700"
                }`}>{TYPES.find((t) => t.key === r.cashflow_type)?.label || r.cashflow_type}</span>
                <span className="text-muted-foreground">{r.cashflow_date}</span>
                <span className="ml-auto font-medium">
                  {isIncome ? "+" : "−"}{inr(Math.abs(r.amount))}
                </span>
                <button onClick={() => del(r.id)} className="text-muted-foreground hover:text-red-600"
                  data-testid={`cashflow-delete-${r.id}`} aria-label="Delete">
                  <Trash size={12} weight="duotone" />
                </button>
              </div>
            );
          })}
          <div className="text-[11px] text-muted-foreground mt-1 text-right">
            Net cashflow to date:
            <span className={`ml-1 font-medium ${net >= 0 ? "text-green-700" : "text-red-700"}`}
              data-testid="cashflow-net">
              {net >= 0 ? "+" : "−"}{inr(Math.abs(net))}
            </span>
          </div>
        </div>
      )}

      {/* Add row */}
      <div className="border hairline p-3 space-y-2" data-testid="cashflow-add-form">
        <div className="text-xs font-medium">Add a cashflow for {propertyName}</div>
        <div className="grid sm:grid-cols-4 gap-2">
          <select value={type} onChange={(e) => setType(e.target.value)}
            className="bg-background border hairline px-2 py-1.5 text-xs"
            data-testid="cashflow-type">
            {TYPES.map((t) => (
              <option key={t.key} value={t.key}>{t.sign} {t.label}</option>
            ))}
          </select>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="bg-background border hairline px-2 py-1.5 text-xs"
            data-testid="cashflow-date" />
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder="Amount (₹)"
            className="bg-background border hairline px-2 py-1.5 text-xs"
            data-testid="cashflow-amount" />
          <button onClick={add} disabled={saving}
            className="btn-primary px-3 py-1.5 text-xs inline-flex items-center justify-center gap-1 disabled:opacity-50"
            data-testid="cashflow-add">
            <Plus size={12} weight="bold" />Add
          </button>
        </div>
        <input value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          className="w-full bg-background border hairline px-2 py-1.5 text-xs"
          data-testid="cashflow-notes" />
      </div>
    </div>
  );
}
